import { Component, inject, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, debounceTime, distinctUntilChanged, firstValueFrom, map, takeUntil } from 'rxjs';
import { UsuarioService } from 'src/app/services/usuario.service'; 
import { TipoDocumento } from 'src/app/models/TipoDocumento';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { normalizeText } from 'src/app/shared/text.util';

@Component({
    selector: 'app-tipos-documentos',
    templateUrl: './tipos-documentos.component.html',
    styleUrls: ['./tipos-documentos.component.css'],
    standalone: false
})
export class TiposDocumentosComponent implements OnInit {
  private _authService = inject(AuthService); 
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private router = inject(Router);
  private _location = inject(Location);
  
  tiposDocumentos: TipoDocumento[] = [];
  tiposDocumentosFiltradas$?: Observable<TipoDocumento[]>;
  searchTerm$ = new BehaviorSubject<string>('');
  error = signal<string>('');
  message = signal<string>('');
  private OnDestroy$ = new Subject();

  constructor(){

  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;
    
    try {
      await this._loadingService.run(async () => {
        // 1) Sesión
        await this._authService.ensureSession(); 
        // 2) Datos
        this.tiposDocumentos = this._usuarioService.tiposDocumentos();
        this.filterList?.();
      });
    } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
        this.router.navigate(['/sign-in']);
    }
  }

  filterList(): void {
    const collator = new Intl.Collator('es', {
      sensitivity: 'base',
      ignorePunctuation: true,
    });

    this.tiposDocumentosFiltradas$ = this.searchTerm$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntil(this.OnDestroy$),
      map(term => {
        const q = normalizeText(term);

        return (this.tiposDocumentos ?? [])
          .filter(tipoDocumento =>
            normalizeText(tipoDocumento.x_descripcion).includes(q)
          )
          .sort((a, b) =>
            collator.compare(a.x_descripcion ?? '', b.x_descripcion ?? '')
          );
      })
    );
  }

  /**
   * Activa/Desactiva un usuario.
   * Recibe el modelo y el nuevo estado (true/false).
   * Hace "optimistic update" y revierte si hay error.
   */
  async activarDesactivar(tipoDocumento: TipoDocumento, checked: boolean): Promise<void> {
    // Guardá el valor previo antes de cambiarlo
    const prev = !!tipoDocumento.b_activo;

    // Optimistic UI: reflejar el cambio en la UI al toque
    tipoDocumento.b_activo = checked;

    try {
      await this._loadingService.run(async () => {
        await firstValueFrom(this._usuarioService.updateTipoDocumento(tipoDocumento));
      });
      this.message.set('Modificación realizada correctamente');
      setTimeout(() => this.message.set(''), 2500);
    } catch (e) {
      // Rollback si falla
      tipoDocumento.b_activo = prev;
      this.error.set('Error al realizar la modificación');
      console.error(e);
    }
  }

  back(){
    this._location.back();
  }
}
